'use client';

import { useState } from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { RecapAsset } from '@/api/trading/types';
import { useRecap } from '@/api/trading/useRecap';
import { ApiPlatform } from '@/api/types';
import { ArrowRight } from '@/blocks/components/Icons/Arrow';
import { LinkAsset, useSignup } from '@/blocks/components/PlatformLink';
import {
  CellActions,
  CellAsset,
  CellHeading,
  CellPct,
  CellPrice,
  CellPriceAndChange,
  Table,
  TableRow,
} from '@/blocks/components/Table';
import {
  getAssetsNamesBySlug,
  getCfdCategorySlugs,
  getCfdHubUrl,
  getHomePagePopularAssetNames,
} from '@/data';
import { LinkI18n } from '@/i18n/routing';
import { EventBlockNames } from '@/providers/analytics/types';
import buttons from '@/styles/button.module.scss';
import context from '@/styles/contexts.module.scss';

import styles from './styles.module.scss';

enum Category {
  'POPULAR',
  'FOREX',
  'INDICES',
  'COMMODITIES',
  'CRYPTO',
  'SHARES',
}

const presets = {
  [Category.POPULAR]: {
    assets: getHomePagePopularAssetNames(),
    hubHref: getCfdHubUrl(),
  },
  [Category.FOREX]: {
    assets: getAssetsNamesBySlug('forex'),
    hubHref: getCfdHubUrl('forex'),
  },
  [Category.INDICES]: {
    assets: getAssetsNamesBySlug('indices'),
    hubHref: getCfdHubUrl('indices'),
  },
  [Category.COMMODITIES]: {
    assets: getAssetsNamesBySlug('commodities'),
    hubHref: getCfdHubUrl('commodities'),
  },
  [Category.CRYPTO]: {
    assets: getAssetsNamesBySlug('crypto'),
    hubHref: getCfdHubUrl('crypto'),
  },
  [Category.SHARES]: {
    assets: getAssetsNamesBySlug('shares'),
    hubHref: getCfdHubUrl('shares'),
  },
};

export function HeroAssetsTable({ markets }: { markets: RecapAsset[] }) {
  const [selectedCategory, setSelectedCategory] = useState<Category>(Category.POPULAR);
  const tr = useTranslations();
  const { openSignupWithAsset } = useSignup();
  const { markets: assets } = useRecap(ApiPlatform.DEVEX, markets);

  const tabs = [
    { value: Category.POPULAR, label: tr.rich('pages.cfd.index.sections.hero.table.tabs.popular') },
    ...getCfdCategorySlugs().map((category) => {
      return {
        forex: {
          value: Category.FOREX,
          label: tr.rich('pages.cfd.index.sections.hero.table.tabs.forex'),
        },
        indices: {
          value: Category.INDICES,
          label: tr.rich('pages.cfd.index.sections.hero.table.tabs.indices'),
        },
        commodities: {
          value: Category.COMMODITIES,
          label: tr.rich('pages.cfd.index.sections.hero.table.tabs.commodities'),
        },
        shares: {
          value: Category.SHARES,
          label: tr.rich('pages.cfd.index.sections.hero.table.tabs.stocks'),
        },
        crypto: {
          value: Category.CRYPTO,
          label: tr.rich('pages.cfd.index.sections.hero.table.tabs.crypto'),
        },
      }[category] as { value: Category; label: string };
    }),
  ];

  const assetsToDisplay = presets[selectedCategory].assets.map((assetName) => ({
    name: assetName,
    ...(assets || []).find((asset) => asset.name === assetName),
  }));

  return (
    <div
      className={classNames(styles.heroBody, context.light)}
      data-event-block-name={EventBlockNames.assets_table}
    >
      <div className={classNames(styles.tabsContainer)}>
        {tabs.map(({ value, label }) => {
          return (
            <button
              key={value}
              className={classNames(buttons.btn, buttons.btnPill, {
                [buttons.btnPillActive]: value === selectedCategory,
              })}
              onClick={() => setSelectedCategory(value)}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div>
        <Table className={classNames(context.light, styles.table)}>
          <CellHeading>{tr.rich('pages.cfd.index.sections.hero.table.headers.assets')}</CellHeading>
          <CellHeading>
            {tr.rich('pages.cfd.index.sections.hero.table.headers.price', { br: (_) => <br /> })}
          </CellHeading>
          <CellHeading>{tr.rich('pages.cfd.index.sections.hero.table.headers.lastPrice')}</CellHeading>
          <CellHeading colSpan={2}>
            {tr.rich('pages.cfd.index.sections.hero.table.headers.change')}
          </CellHeading>
          {assetsToDisplay.map((asset) => {
            return (
              <TableRow
                className={styles.tableRow}
                key={asset.name}
                onClick={(e) => {
                  if (!(e.target instanceof HTMLAnchorElement)) {
                    openSignupWithAsset({ asset, newTab: e.metaKey });
                  }
                }}
              >
                <CellAsset asset={asset as RecapAsset} className={styles.cellAsset} />
                <CellPriceAndChange
                  price={asset.price}
                  change={asset.change24h}
                  priceScale={asset.priceScale}
                  icon={false}
                />
                <CellPrice className={styles.cellPrice} price={asset.price} priceScale={asset.priceScale} />
                <CellPct pct={asset.change24h} className={styles.cellPct} />
                <CellActions>
                  <LinkAsset
                    asset={asset}
                    className={classNames(buttons.btn, buttons.btnXXS, buttons.btnOutline)}
                    data-event-label={tr.rich('en.pages.cfd.index.sections.hero.table.rowCta')}
                  >
                    {tr.rich('pages.cfd.index.sections.hero.table.rowCta')}
                  </LinkAsset>
                </CellActions>
              </TableRow>
            );
          })}
        </Table>
      </div>
      <div className={classNames(styles.ctaContainer)}>
        <LinkI18n
          href={presets[selectedCategory].hubHref}
          className={classNames(buttons.btn, buttons.btnLink, buttons.btnM, buttons.btnIconRight)}
        >
          {tr.rich('pages.cfd.index.sections.hero.table.cta')} <ArrowRight />
        </LinkI18n>
      </div>
    </div>
  );
}
